import { Route, Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetProductQuery as GetProduct, ProductType } from '__generated__/graphql';

import EditProductInfoForm from './EditProductInfoForm';
import EditProductPricingDonationForm from './EditProductPricingDonationForm';
import EditProductPricingFeeForm from './EditProductPricingFeeForm';
import EditProductPricingItemForm from './EditProductPricingItemForm';
import EditProductRestrictionsForm from './EditProductRestrictionsForm';
import EditProductVariantsForm from './EditProductVariantsForm';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../useProject';

type Event = GetProduct['event'];
type Product = GetProduct['product'];

export interface EditProductFormProps {
  product: Product;
  event: Event;
}

const EditProductForm = ({ product, event }: EditProductFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
      <UI.Nav>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/products/${product.id}`}
          exact
        >
          {t('basic_info')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/products/${product.id}/pricing`}
        >
          {t('pricing')}
          {product.type !== ProductType.donation && product.promotions.length > 1 && (
            <UI.Small sc={{ muted: true }} style={{ fontWeight: 400 }}>
              {` (${formatNumber(product.promotions.length)})`}
            </UI.Small>
          )}
        </UI.NavLink>
        {product.type === ProductType.item && (
          <UI.NavLink
            to={`/${project.id}/events/${event.id}/products/${product.id}/restrictions`}
          >
            {t('restrictions')}
          </UI.NavLink>
        )}
        {product.type === ProductType.item && (
          <UI.NavLink
            to={`/${project.id}/events/${event.id}/products/${product.id}/variants`}
          >
            {t('product_variants')}
            {product.product_variants.length > 0 && (
              <UI.Small sc={{ muted: true }} style={{ fontWeight: 400 }}>
                {` (${formatNumber(product.product_variants.length)})`}
              </UI.Small>
            )}
          </UI.NavLink>
        )}
      </UI.Nav>

      <UI.Div>
        <Switch>
          <Route path="/:projectId/events/:eventId/products/:productId/pricing">
            {product.type === ProductType.item && (
              <EditProductPricingItemForm product={product} event={event} />
            )}
            {product.type === ProductType.fee && (
              <EditProductPricingFeeForm product={product} event={event} />
            )}
            {product.type === ProductType.donation && (
              <EditProductPricingDonationForm product={product} event={event} />
            )}
          </Route>
          {product.type === ProductType.item && (
            <Route path="/:projectId/events/:eventId/products/:productId/variants">
              <EditProductVariantsForm product={product} event={event} />
            </Route>
          )}
          {product.type === ProductType.item && (
            <Route path="/:projectId/events/:eventId/products/:productId/restrictions">
              <EditProductRestrictionsForm product={product} event={event} />
            </Route>
          )}
          <Route path="/:projectId/events/:eventId/products/:productId">
            <EditProductInfoForm product={product} event={event} />
          </Route>
        </Switch>
      </UI.Div>
    </UI.FormGrid>
  );
};
export default EditProductForm;
